import axios, { AxiosError } from 'axios'
import Cookies from 'js-cookie'
import createAuthRefreshInterceptor from 'axios-auth-refresh'
import * as amplitude from '@amplitude/analytics-browser'
import * as Sentry from '@sentry/react'

import { RefreshTokenRequest, RefreshTokenResponse } from '@models/Account'
import { clearTokens, setTokens } from './token'
import { API_ADDRESS } from '../constants'

import router from '@src/Routes'
import routes from '@src/Routes/routes'
import queryClient from '@helpers/queryClient'
import useStore from '@src/store'

export const REFRESH_TOKEN_PATH = '/api/token/refresh/'

const axiosInstance = axios.create({
  baseURL: API_ADDRESS,
  withCredentials: true,
})

const refreshToken = async (): Promise<string | void> => {
  const refreshToken = localStorage.getItem('refreshToken') || Cookies.get('refreshToken')

  if (!refreshToken || refreshToken === 'undefined') {
    throw new Error('No refresh token available')
  }
  try {
    const refreshResponse = await axios.post<RefreshTokenResponse>(
      REFRESH_TOKEN_PATH,
      {
        refresh: refreshToken,
      } as RefreshTokenRequest,
      {
        baseURL: API_ADDRESS,
        withCredentials: true,
      },
    )

    setTokens(refreshResponse.data.access, refreshResponse.data.refresh)
    return refreshResponse.data.access
  } catch (err) {
    if (err) {
      clearTokens()
      delete axiosInstance.defaults.headers.Authorization
      amplitude.reset()
      localStorage.clear()
      useStore.getState().reset()
      queryClient.removeQueries()
      await router.navigate(routes.public.login, {
        state: {
          prev: window.location.pathname,
        },
      })
      Sentry.captureException(err)
      throw err
    }
  }
}

createAuthRefreshInterceptor(axiosInstance, refreshToken, {
  shouldRefresh: (error: AxiosError) => {
    const response = error.response
    const isIntegrationUrl = ['quickbooks', 'xero', 'softledger'].some((integration) =>
      response?.config?.url?.includes(integration),
    )
    return response?.status === 401 && !isIntegrationUrl
  },
  onRetry: (requestConfig) => {
    if (requestConfig.headers) {
      requestConfig.headers.Authorization = axiosInstance.defaults.headers.Authorization
    }
    return requestConfig
  },
})

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('token') || Cookies.get('token')

  if (token && token !== 'undefined') {
    config.headers.Authorization = `Token ${token}`
  }

  return config
})

export default axiosInstance
